import { auth, db } from './config';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  getDoc,
  orderBy,
  getCountFromServer,
} from 'firebase/firestore';
import type { Appointment, Patient, QueueEntry } from '@/types/models';
import { AppointmentHistoryService } from './appointmentHistory';

const NO_SHOW_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes (T096)

export const QueueService = {
  async addToQueue(
    clinicId: string,
    data: Omit<QueueEntry, 'id' | 'status' | 'arrivedAt' | 'ticket' | 'waitTimeMinutes'>,
  ) {
    const newDocRef = doc(collection(db, 'clinics', clinicId, 'queue'));

    // Generate sequential ticket: A001, A002, etc. — reset daily
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();

    const todayQueue = query(
      collection(db, 'clinics', clinicId, 'queue'),
      where('unitId', '==', data.unitId),
      where('arrivedAt', '>=', todayMs),
    );
    const count = (await getCountFromServer(todayQueue)).data().count;
    const ticket = `A${String(count + 1).padStart(3, '0')}`;

    // Onde o paciente está: local de quem faz o check-in (recepção) ou, se
    // for o médico, onde os pacientes dele chegaram hoje. Separa os pop-ups
    // das recepções de lugares diferentes. Best-effort: sem local = aviso
    // vai pra todas as recepções do escopo, como antes.
    let serviceLocal = data.serviceLocal;
    if (!serviceLocal) {
      try {
        serviceLocal = await this.resolveLocal(clinicId, data.professionalId);
      } catch (err) {
        console.warn('[queue] local de atendimento não resolveu:', err);
      }
    }

    const queueData = {
      ...data,
      ...(serviceLocal ? { serviceLocal } : {}),
      id: newDocRef.id,
      clinicId,
      ticket,
      status: 'WAITING' as const,
      arrivedAt: Date.now(),
      waitTimeMinutes: 0,
    };
    await setDoc(newDocRef, queueData);
    return queueData;
  },

  /**
   * Local de atendimento de quem está agindo agora: o da conta logada
   * (recepção, Equipe) e, se ela não tiver (médico), o do médico informado
   * — ver [[currentLocalOf]].
   */
  async resolveLocal(clinicId: string, professionalId?: string): Promise<string | undefined> {
    const uid = auth.currentUser?.uid;
    if (uid) {
      const me = await getDoc(doc(db, 'clinics', clinicId, 'professionals', uid));
      const mine = (me.data() as { serviceLocal?: unknown } | undefined)?.serviceLocal;
      if (typeof mine === 'string' && mine) return mine;
    }
    return professionalId ? this.currentLocalOf(clinicId, professionalId) : undefined;
  },

  /**
   * Onde o médico está atendendo agora: o local do último paciente dele que
   * fez check-in hoje. O médico circula entre lugares (e às vezes leva o
   * mesmo notebook), então o local dele não é configuração — é a fila.
   * Filtro por médico no cliente: a fila do dia é pequena e evita índice.
   */
  async currentLocalOf(clinicId: string, professionalId: string): Promise<string | undefined> {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const snap = await getDocs(query(
      collection(db, 'clinics', clinicId, 'queue'),
      where('arrivedAt', '>=', start.getTime()),
    ));
    let best: QueueEntry | undefined;
    snap.docs.forEach((d) => {
      const e = d.data() as QueueEntry;
      if (e.professionalId !== professionalId || !e.serviceLocal) return;
      if (!best || e.arrivedAt > best.arrivedAt) best = e;
    });
    return best?.serviceLocal;
  },

  async updateQueueEntry(clinicId: string, entryId: string, data: Partial<QueueEntry>) {
    const docRef = doc(db, 'clinics', clinicId, 'queue', entryId);
    await updateDoc(docRef, data);
  },

  async getActiveQueue(clinicId: string, unitId?: string) {
    const constraints = [
      where('status', 'in', ['WAITING', 'CALLED', 'IN_SERVICE']),
    ];
    if (unitId) {
      constraints.push(where('unitId', '==', unitId));
    }

    const q = query(
      collection(db, 'clinics', clinicId, 'queue'),
      ...constraints,
      orderBy('arrivedAt', 'asc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => {
      const entry = d.data() as QueueEntry;
      // Calculate current wait time
      entry.waitTimeMinutes = Math.round((Date.now() - entry.arrivedAt) / 60000);
      return entry;
    });
  },

  /**
   * Entradas da fila de HOJE (chegada desde 0h). Mesma consulta do painel do
   * dia (range simples em `arrivedAt`, sem índice composto); o status filtra
   * quem chama. Usado pelo "Atlas, abre o próximo" (`pickNextToOpen`).
   */
  async getToday(clinicId: string): Promise<QueueEntry[]> {
    const inicio = new Date();
    inicio.setHours(0, 0, 0, 0);
    const snap = await getDocs(query(
      collection(db, 'clinics', clinicId, 'queue'),
      where('arrivedAt', '>=', inicio.getTime()),
    ));
    return snap.docs.map((d) => d.data() as QueueEntry);
  },

  async callPatient(clinicId: string, entryId: string, room: string, calledBy?: string) {
    await this.updateQueueEntry(clinicId, entryId, {
      status: 'CALLED',
      calledAt: Date.now(),
      room,
      ...(calledBy ? { calledBy } : {}),
    } as Partial<QueueEntry>);
  },

  /**
   * Encontra a entrada ativa (WAITING/CALLED/IN_SERVICE) de um agendamento, se existir.
   * Filtra status client-side pra evitar criar um índice composto novo —
   * espera-se no máximo 1-2 docs por appointmentId no dia.
   */
  async findActiveByAppointment(
    clinicId: string,
    appointmentId: string,
  ): Promise<QueueEntry | null> {
    const q = query(
      collection(db, 'clinics', clinicId, 'queue'),
      where('appointmentId', '==', appointmentId),
    );
    const snap = await getDocs(q);
    const active = snap.docs.find((d) => {
      const s = (d.data() as QueueEntry).status;
      return s === 'WAITING' || s === 'CALLED' || s === 'IN_SERVICE';
    });
    return active ? (active.data() as QueueEntry) : null;
  },

  /**
   * Idempotente: cria a entrada na fila pra esse agendamento (com nomes
   * de paciente e médico denormalizados) e bumpa o status do appointment
   * pra EM_ESPERA quando ainda estava AGENDADA/CONFIRMADA. Se o paciente
   * já passou pelo totem antes (entry ativa existe), devolve a existente
   * sem mexer.
   *
   * Usado por dois caminhos da agenda — modal de detalhes e painel
   * lateral rápido — pra manter o comportamento sincronizado.
   */
  async checkInFromAppointment(
    clinicId: string,
    appointment: Appointment,
    patient: Patient | null,
    actor: { uid: string; name?: string },
  ): Promise<QueueEntry> {
    const existing = await this.findActiveByAppointment(clinicId, appointment.id);
    if (existing) return existing;

    // Best-effort: denormaliza o nome do médico pra QueuePage exibir sem
    // read extra. Falha (rules/permissão) não derruba o check-in.
    let professionalName: string | undefined;
    if (appointment.professionalId) {
      try {
        const profSnap = await getDoc(
          doc(db, 'clinics', clinicId, 'professionals', appointment.professionalId),
        );
        professionalName = (profSnap.data() as { name?: string } | undefined)?.name;
      } catch (err) {
        console.warn('[queue:checkin] nome do médico não resolveu:', err);
      }
    }

    const entry = (await this.addToQueue(clinicId, {
      clinicId,
      unitId: appointment.unitId,
      patientId: appointment.patientId,
      patientName: patient?.name || appointment.patientName,
      appointmentId: appointment.id,
      professionalId: appointment.professionalId ?? '',
      professionalName,
      createdAt: Date.now(),
    })) as QueueEntry;

    // checkedInAt espelha o arrivedAt da fila no próprio agendamento —
    // é o que alimenta a minutagem de espera (chegada → atendimento)
    // sem precisar consultar a collection queue depois.
    const patch: Partial<Appointment> = {};
    if (!appointment.checkedInAt) patch.checkedInAt = entry.arrivedAt;
    if (appointment.status === 'AGENDADA' || appointment.status === 'CONFIRMADA') {
      patch.status = 'EM_ESPERA';
    }
    if (Object.keys(patch).length > 0) {
      try {
        await AppointmentHistoryService.updateTracked(
          clinicId,
          appointment.id,
          patch,
          { uid: actor.uid, name: actor.name, action: 'check-in' },
        );
      } catch (err) {
        console.warn('[queue:checkin] status update falhou:', err);
      }
    }

    return entry;
  },

  /**
   * Oftalmo: o médico envia o paciente de volta pra recepção pra DILATAR
   * a pupila. Recoloca a entrada na sala de espera (WAITING) e carimba o
   * pedido de dilatação — isso dispara o alerta da recepção (CallAlertWatcher)
   * e o selo "Dilatar" na fila. `requestedBy` evita mostrar o alerta a quem
   * pediu (mesmo padrão de `callPatient`). `room` (sala do médico) é só
   * informativo pro alerta. Não usa `undefined` pra limpar campos antigos
   * (`calledAt`/`room`) porque o Firestore deste projeto não tem
   * `ignoreUndefinedProperties`; status=WAITING já neutraliza a exibição.
   */
  async requestDilation(
    clinicId: string,
    entryId: string,
    requestedBy: string,
    room?: string,
  ) {
    await this.updateQueueEntry(clinicId, entryId, {
      status: 'WAITING',
      dilationRequestedAt: Date.now(),
      dilationRequestedBy: requestedBy,
      ...(room ? { dilationRoom: room } : {}),
    } as Partial<QueueEntry>);
  },

  async startService(clinicId: string, entryId: string) {
    await this.updateQueueEntry(clinicId, entryId, { status: 'IN_SERVICE' });
  },

  async completeService(clinicId: string, entryId: string) {
    await this.updateQueueEntry(clinicId, entryId, { status: 'COMPLETED' });
  },

  /**
   * T096: Mark no-show and optionally re-queue at the end.
   * Automatically called if patient doesn't respond within 5 minutes of being called.
   */
  async markNoShow(clinicId: string, entryId: string) {
    await this.updateQueueEntry(clinicId, entryId, { status: 'NO_SHOW' });
  },

  /**
   * T096: Re-queue a no-show patient at the end of the queue.
   */
  async requeue(clinicId: string, entryId: string) {
    await this.updateQueueEntry(clinicId, entryId, {
      status: 'WAITING',
      calledAt: undefined,
      room: undefined,
      arrivedAt: Date.now(), // Move to end of queue
    } as Partial<QueueEntry>);
  },

  /**
   * T096: Check for called patients that have timed out (> 5 min since called).
   * Returns entries that should be marked as no-show.
   */
  async checkNoShowTimeouts(clinicId: string): Promise<QueueEntry[]> {
    const q = query(
      collection(db, 'clinics', clinicId, 'queue'),
      where('status', '==', 'CALLED'),
    );
    const snapshot = await getDocs(q);
    const now = Date.now();

    return snapshot.docs
      .map((d) => d.data() as QueueEntry)
      .filter((entry) => {
        const calledAt = entry.calledAt ?? 0;
        return now - calledAt > NO_SHOW_TIMEOUT_MS;
      });
  },
};
